import {
  CUSTOM_STICKER_MAX, CUSTOM_STICKER_PRICE, HORNS, MAX_UPGRADE_LEVEL, PAINTS, POWER_UPS, STICKERS, formatCedis, powerUpSeconds, upgradeItemId, upgradePrice,
  type GarageKind,
} from '@accra/shared';
import { customStickers, isOwned, upgradeLevel, type GarageState } from '../../meta/garage';
import { el } from '../components/el';

export interface RowHandlers {
  equip(kind: GarageKind, id: string, text?: string): void;
  buy(kind: GarageKind, id: string, text?: string): void;
  previewHorn(id: string): void;
}

const UNLOCK_LABELS = { streak7: '🔥 7-day streak', march6: '🇬🇭 6 March gift' } as const;

function actionButton(label: string, className: string, onClick?: () => void, disabled = false): HTMLButtonElement {
  const button = el('button', `garage-action ${className}`, label);
  button.disabled = disabled || !onClick;
  if (onClick) button.addEventListener('click', onClick);
  return button;
}

/** "In use", "Use", a price to buy, or a locked label. */
function action(state: GarageState, wallet: number, kind: GarageKind, id: string, price: number, h: RowHandlers, unlock?: keyof typeof UNLOCK_LABELS): HTMLButtonElement {
  const equipped = kind === 'paint' ? state.equipped.paint : kind === 'horn' ? state.equipped.horn : state.equipped.sticker;
  if (equipped === id && !(kind === 'sticker' && id === 'custom')) return actionButton('In use', 'in-use');
  if (isOwned(state, kind, id)) return actionButton('Use', 'use', () => h.equip(kind, id));
  if (unlock) return actionButton(UNLOCK_LABELS[unlock], 'locked');
  const short = wallet < price;
  const button = actionButton(formatCedis(price), 'buy', () => h.buy(kind, id), short);
  if (short) button.title = wallet < 0 ? 'Pay off what you owe first' : 'Not enough money yet';
  return button;
}

function row(swatch: HTMLElement, name: string, blurb: string, button: HTMLElement, extra?: HTMLElement): HTMLElement {
  const item = el('div', 'garage-row');
  const text = el('div', 'garage-text');
  text.append(el('strong', '', name));
  if (blurb) text.append(el('small', '', blurb));
  item.append(swatch, text);
  if (extra) item.append(extra);
  item.append(button);
  return item;
}

function paintSwatch(body: string, stripes: readonly string[]): HTMLElement {
  const swatch = el('div', 'swatch');
  swatch.style.background = body;
  stripes.forEach((color) => {
    const band = el('i');
    band.style.background = color;
    swatch.append(band);
  });
  return swatch;
}

export function paintRows(state: GarageState, wallet: number, h: RowHandlers): HTMLElement[] {
  return PAINTS.map((p) => row(paintSwatch(p.body, p.stripes), p.name, '', action(state, wallet, 'paint', p.id, p.price, h, p.unlock)));
}

export function stickerRows(state: GarageState, wallet: number, h: RowHandlers): HTMLElement[] {
  const rows = STICKERS.map((s) => row(el('div', 'swatch sticker', '✦'), `“${s.text}”`, '', action(state, wallet, 'sticker', s.id, s.price, h)));
  for (const text of customStickers(state)) {
    const inUse = state.equipped.sticker === 'custom' && state.equipped.customSticker === text;
    rows.push(row(el('div', 'swatch sticker', '✎'), `“${text}”`, 'Your own words', inUse ? actionButton('In use', 'in-use') : actionButton('Use', 'use', () => h.equip('sticker', 'custom', text))));
  }
  const input = el('input', 'custom-sticker');
  input.maxLength = CUSTOM_STICKER_MAX;
  input.placeholder = 'Write your own…';
  const make = actionButton(formatCedis(CUSTOM_STICKER_PRICE), 'buy', () => {
    const text = input.value.trim().toUpperCase();
    if (text) h.buy('sticker', 'custom', text);
  }, wallet < CUSTOM_STICKER_PRICE);
  rows.push(row(el('div', 'swatch sticker', '+'), 'Custom sticker', `Up to ${CUSTOM_STICKER_MAX} letters on your back window`, make, input));
  return rows;
}

export function hornRows(state: GarageState, wallet: number, h: RowHandlers): HTMLElement[] {
  return HORNS.map((horn) => {
    const play = el('button', 'swatch play', '▶');
    play.addEventListener('click', () => h.previewHorn(horn.id));
    return row(play, horn.name, horn.blurb, action(state, wallet, 'horn', horn.id, horn.price, h));
  });
}

/** Each power-up: how long it lasts now → after the next level, five level pips, and the price. */
export function upgradeRows(state: GarageState, wallet: number, h: RowHandlers): HTMLElement[] {
  return POWER_UPS.map((power) => {
    const level = upgradeLevel(state, power.id);
    const now = powerUpSeconds(power.id, level);
    const blurb = level >= MAX_UPGRADE_LEVEL ? `Lasts ${now} s · max level` : `Lasts ${now} s → ${powerUpSeconds(power.id, level + 1)} s`;
    const pips = el('div', 'pips');
    for (let i = 1; i <= MAX_UPGRADE_LEVEL; i += 1) pips.append(el('i', i <= level ? 'on' : ''));
    const price = upgradePrice(level + 1);
    const button = price === null
      ? actionButton('Max', 'in-use')
      : actionButton(formatCedis(price), 'buy', () => h.buy('upgrade', upgradeItemId(power.id, level + 1)), wallet < price);
    if (price !== null && wallet < price) button.title = wallet < 0 ? 'Pay off what you owe first' : 'Not enough money yet';
    return row(el('div', 'swatch sticker', power.icon), power.name, blurb, button, pips);
  });
}
